import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../../../lib/i18n';
import { ENTITY_TYPE, useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { useRoutines } from '../../../../lib/routines';
import { queryClient, queryKeys } from '../../../../lib/query-client';
import { calendarProviderToProviderId, fetchConnectors, type ConnectorsStatus } from '../../../../lib/connectors';
import { useConnectConnector } from '../../../../lib/use-connect-connector';
import { connectHrefFor } from '../../../../lib/suggested-task-connect';
import {
  BAND_CONNECT_POLL_INTERVAL_MS,
  bandAppEffectiveStatus,
  bandCardSeenFields,
  bandConnectProgress,
  bandConnectTarget,
  bandConnectUrlFrom,
  connectedToolkitsOf,
  shouldPollBandConnect,
  type BandCardData,
} from '../../../../lib/band-cards';
import { AgentJoinCard } from '../../AgentJoinCard';
import { Button, Card, Typography } from '../../ui';
import { CHAT_HOME_BI } from '../message-utils';
import { BandAgentRows, BandAppChips } from './BandAgentRows';
import { connectorRoutinesForSlugs } from '../../../../lib/routine-groups';
import styles from './BandCard.module.css';

const cachedConnectors = () => queryClient.getQueryData<ConnectorsStatus>(queryKeys.connectors)?.connectors ?? [];
const refreshConnectors = () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors });

/**
 * The band once it exists, drawn on the turn the connect chain reports back — the whole team and every
 * app it runs on, under one reply.
 *
 * <h2>The layout (Figma 18942:127712 web, 18942:128425 mobile)</h2>
 *
 * The name, then the orbit beside a column holding the goal line, the team and the home-page line — the
 * orbit stacks above that column when the card is too narrow for both — then the connectors as one
 * wrapping cluster of named chips, then a footer with the progress and the one connect action. The
 * goal's task list and the routines are not drawn: the reply beside the card walks the plan, and the
 * routines live on the Routines screen.
 *
 * <h2>The chips are LIVE</h2>
 *
 * A connected app carries the lime check. The marker is stamped when the chain is handed over, so every
 * status read goes through `bandAppEffectiveStatus` against the shared connectors query, never
 * `app.status` directly — otherwise an app the owner just connected would still read as not connected.
 *
 * <h2>The footer only exists while something is left to connect</h2>
 *
 * Skipped and failed apps count as left: the owner may want them now, and this card is where they would
 * look. What the button does is `bandConnectTarget`: re-open the chain while it still has apps to walk,
 * otherwise connect the first leftover app through the ordinary single-connector flow.
 */
export function BandCard({
  card,
  conversationId,
  onConnected,
}: {
  card: BandCardData;
  conversationId?: string;
  /** Arms the assistant-wake after a single connect lands — the chat surface's `noteConnected`. */
  onConnected?: (name: string) => void;
}) {
  const { t } = useTranslation();
  const { state } = useRoutines();
  const { logPageClickBi } = useGenericBi();
  // Set when the owner opens the connect chain, which is what ARMS the poll. Before that there is
  // nothing in flight to watch, and polling on every band card in the transcript would be a request per
  // card every few seconds for a chain nobody started.
  const [connectStartedAt, setConnectStartedAt] = useState<number | null>(null);

  const routines = state.kind === 'ready' ? state.routines : [];
  const widgetCount = card.homeWidgets?.length ?? 0;

  // The SAME connectors read the in-chat connector card and the Connectors screen share (one query key,
  // so the two can never disagree about whether an app is on). React Query owns the poll and the focus
  // refetch — the chain runs in another browser tab, so coming back is when the answer changed.
  const { data: connectors } = useQuery({
    queryKey: queryKeys.connectors,
    queryFn: fetchConnectors,
    refetchOnWindowFocus: connectStartedAt !== null,
    // Read off the FRESHEST payload every tick, so the loop stops the moment the chain has nothing left
    // to walk. Bounded, so an abandoned browser tab does not leave a request loop behind.
    refetchInterval: (latest) => {
      if (connectStartedAt === null) return false;
      const live = connectedToolkitsOf(latest?.connectors);
      if (bandConnectTarget(card.apps, card.connectUrl, live).kind !== 'chain') return false;
      return shouldPollBandConnect(connectStartedAt, Date.now()) ? BAND_CONNECT_POLL_INTERVAL_MS : false;
    },
  });
  // The single-app connect: the same rail the Connectors page and the suggested-task chip run, with its
  // own popup and poll. It refreshes the shared query, so the chip flips to checked when it lands.
  const { connect } = useConnectConnector({ getConnectors: cachedConnectors, refresh: refreshConnectors, onConnected });

  const connectedToolkits = useMemo(() => connectedToolkitsOf(connectors?.connectors), [connectors]);
  const target = bandConnectTarget(card.apps, card.connectUrl, connectedToolkits);
  const progress = bandConnectProgress(card.apps, connectedToolkits);
  // "Connect all" only when the chain will walk more than one app; a single connect is always one app.
  const connectActionLabel = t(target.kind === 'chain' && target.apps.length > 1 ? 'band.card.connectAll' : 'common.connect');
  const targetApps = target.kind === 'chain' ? target.apps : target.kind === 'single' ? [target.app] : [];
  const connectAriaLabel = `${connectActionLabel}: ${targetApps.map((app) => app.name).join(', ')}`;
  // A single connect needs the app's row in the connectors list. Before that list loads — or for a toolkit
  // it does not know — the button falls back to that connector's card on the Connectors page.
  const singleConnector =
    target.kind === 'single'
      ? connectors?.connectors.find((connector) => connector.toolkit.toLowerCase() === target.app.toolkit.toLowerCase())
      : undefined;
  // Apple connects through an inline Apple ID form that only the Connectors page renders, so it goes there.
  const singleNeedsPage = !singleConnector || calendarProviderToProviderId(singleConnector.calendarProvider) === 'apple';
  const singleHref = target.kind === 'single' && singleNeedsPage ? connectHrefFor(target.app.toolkit) : null;

  // Every routine standing behind this band's apps, for the impression. The card no longer draws them,
  // but the event keeps the field so the band's reporting does not change shape.
  const cardRoutineNames = useMemo(
    () =>
      connectorRoutinesForSlugs(
        routines,
        card.apps.flatMap((app) => [app.toolkit, app.group]),
      ).map((routine) => routine.name ?? routine.id),
    [routines, card.apps],
  );
  const seenRef = useItemSeenBi('band_card', {
    ...(conversationId ? { entity: { id: conversationId, type: ENTITY_TYPE.conversation } } : {}),
    additionalFields: bandCardSeenFields(card, cardRoutineNames),
  });

  // One chip tapped: that app's own connect flow, independent of the chain behind "Connect all". Same
  // rail as the single-app button — the connectors row when we can start it here, else that connector's
  // card on the Connectors page (also where Apple's inline form lives).
  const onSelectApp = (app: BandCardData['apps'][number]) => {
    logPageClickBi(
      { ...CHAT_HOME_BI, button_name: 'band-card-connect-app', ...(conversationId ? { conversation_id: conversationId } : {}) },
      { additionalFields: { band: card.name, connectors: app.toolkit, toolkit: app.toolkit, pending: '1', via: 'chip' } },
    );
    const row = connectors?.connectors.find((connector) => connector.toolkit.toLowerCase() === app.toolkit.toLowerCase());
    if (row && calendarProviderToProviderId(row.calendarProvider) !== 'apple') {
      connect(row);
      return;
    }
    const href = connectHrefFor(app.toolkit);
    if (href) window.open(href, '_blank');
  };

  const onConnectClick = () => {
    logPageClickBi(
      { ...CHAT_HOME_BI, button_name: 'band-card-connect-all', ...(conversationId ? { conversation_id: conversationId } : {}) },
      {
        additionalFields: {
          band: card.name,
          // WHICH connectors this opens, not just how many — the point of the click is the set it walks.
          connectors: targetApps.map((app) => app.toolkit).join(','),
          pending: String(targetApps.length),
          via: target.kind,
        },
      },
    );
    if (target.kind === 'chain' && card.connectUrl) {
      // Arms the poll — nothing above watches the connectors list until this happens.
      setConnectStartedAt(Date.now());
      // `window.open`, NOT an <a target="_blank">, because the chain's finish page closes the tab when it
      // is done: a browser lets only a window that SCRIPT opened close itself. `noopener` would sever the
      // same relationship, so it is deliberately absent — the page being opened is our own.
      window.open(bandConnectUrlFrom(card.connectUrl, 'web'), '_blank');
    } else if (singleConnector && !singleHref) {
      connect(singleConnector);
    }
  };

  return (
    <Card component="section" ref={seenRef} className={styles.card} aria-label={t('band.card.accessibilityLabel')} data-testid="chat-band-card">
      <Typography variant="h3" className={styles.name}>
        {card.name}
      </Typography>
      <div className={styles.hero}>
        {/* The band arriving IS the product's signature moment, so it gets the same orbit the "joined your
            team" card plays: the whole band flies into the constellation at once. `showExistingTeam={false}`
            is web's spelling of native's empty `existingAgents` — this card is about THIS group landing.
            Drawn flush on the card's white (`hostSized`), at the size this stylesheet gives it. */}
        <AgentJoinCard agents={card.agents} showExistingTeam={false} embedded hostSized className={styles.orbit} />
        <div className={styles.details}>
          {card.goal && (
            <Typography variant="p2" className={styles.goal}>
              {card.goal.title}
            </Typography>
          )}
          {/* The names have to be READABLE — the orbit's nodes carry no labels, so without this the owner
              watches anonymous dots and never learns who joined. */}
          <div className={styles.section} role="group" aria-label={t('band.offerCard.team')}>
            <Typography variant="p3" component="h4" className={styles.sectionLabel}>
              {t('band.offerCard.teamLabel')}
            </Typography>
            <BandAgentRows agents={card.agents} />
          </div>
          {widgetCount > 0 && (
            <Typography variant="p3" color="secondary" className={styles.widgetLine}>
              {t('connectors.card.homePageSection')} — {t(`packs.count.homeCards.${widgetCount === 1 ? 'one' : 'other'}`, { count: widgetCount })}
            </Typography>
          )}
        </div>
      </div>
      {card.apps.length > 0 && (
        <div className={styles.connect}>
          <div className={styles.section}>
            {/* The same key the offer card uses, so the two band cards cannot name the same row differently. */}
            <Typography variant="p3" component="h4" className={styles.sectionLabel}>
              {t('band.offerCard.apps')}
            </Typography>
            <BandAppChips
              apps={card.apps}
              ariaLabel={t('band.offerCard.apps')}
              isConnected={(app) => bandAppEffectiveStatus(app, connectedToolkits) === 'connected'}
              onSelectApp={onSelectApp}
            />
          </div>
          {target.kind !== 'none' && (
            <div className={styles.footer}>
              {/* Only once something has landed: "0 of 4 connected" on first sight is a progress line for
                  work the owner has not been asked to start yet. The empty span keeps the button on the end. */}
              {progress.done > 0 ? (
                <Typography variant="p2" color="secondary" className={styles.progress}>
                  {t('band.card.connectProgress', progress)}
                </Typography>
              ) : (
                <span className={styles.progress} />
              )}
              {/* The label names the apps to a screen reader, since the chips are separate elements. */}
              {singleHref ? (
                <Button size="small" className={styles.connectAll} href={singleHref} aria-label={connectAriaLabel} onClick={onConnectClick}>
                  {connectActionLabel}
                </Button>
              ) : (
                <Button size="small" className={styles.connectAll} aria-label={connectAriaLabel} onClick={onConnectClick}>
                  {connectActionLabel}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
